import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import html2pdf from 'html3pdf';

import { State } from 'redux/initialState';
import { setModal } from 'modules/_shared/redux/modalActions';
import { useTranslate } from 'hooks/useTranslate';
import { addDocument } from 'modules/documents/redux/documentActions';
import { getDocumentReplacedWithTags } from 'modules/documents/api/replaceDocumentTags';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';
import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { getLinkedTo } from 'modules/documents/components/Modals/UploadDocumentModal/utils/getLinkedTo';
import { TradingOfferOperation } from 'types/TradingOfferOperation';
import { updateTradingOfferOperation } from 'modules/trading/redux/tradingOfferOperationActions';
import getOperationReference from 'modules/trading/utils/getOperationReference';

import { CompleteOperationModalContextType } from '../types/CompleteOperationModalContextType';
import { completeOperationModalInitValue } from '../constants/completeOperationModalInitValue';

const CompleteOperationModalContext =
  createContext<CompleteOperationModalContextType>(
    completeOperationModalInitValue
  );

type ProviderProps = {
  children: ReactNode;
  operation: TradingOfferOperation;
  onSuccess?: () => void;
};

export const CompleteOperationModalProvider: FC<ProviderProps> = ({
  children,
  operation,
  onSuccess,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const [isLoading, setIsLoading] = useState(false);

  const buyer =
    operation.buyer && typeof operation.buyer !== 'string'
      ? operation.buyer
      : null;
  const seller =
    operation.seller && typeof operation.seller !== 'string'
      ? operation.seller
      : null;

  const buyerName = buyer?.name || '';
  const sellerName = seller?.name || '';

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleConfirm = async () => {
    if (!actualSociety) return;

    const templateId = actualSociety?.templates?.equityMarketPurchaseContract;
    if (!templateId) {
      errorSwal(t('ErrorNoEquityMarketTemplate'));
      return;
    }

    setIsLoading(true);

    try {
      const documentUpdated = await (dispatch as any)(
        getDocumentReplacedWithTags({
          documentId: templateId,
          sources: {
            society: actualSociety['_id'],
            tradingOfferOperation: operation._id,
          },
          language: 'es',
        })
      );

      if (!documentUpdated?.html) {
        errorSwal(t('ErrorNoEquityMarketTemplate'));
        setIsLoading(false);
        return;
      }

      const operationReference = getOperationReference(operation);

      const pdfOptions = {
        filename: `${t('EquityMarketPurchaseContract')}.pdf`,
        margin: [10, 10, 10, 10],
        pagebreak: { mode: 'avoid-all' },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      };

      const fileBlob = await html2pdf()
        .from(documentUpdated?.html)
        .set(pdfOptions)
        .output('blob');

      const contractFile = new File(
        [fileBlob],
        `${t('EquityMarketPurchaseContract')}.pdf`,
        { type: 'application/pdf' }
      );

      const linkedTo = getLinkedTo({
        tradingOfferOperationId: operation._id,
      });

      const newDocumentData = {
        file: contractFile,
        name: `${t('EquityMarketPurchaseContract')} ${operationReference}`,
        date: new Date(),
        size: contractFile.size,
        author: user?.['_id'],
        society: actualSociety['_id'],
        fileType: fileTypes.PDF,
        category: documentTypes.DOCUMENTS,
        subcategory: 'EQUITY_MARKET_PURCHASE_CONTRACT',
        description: t('EquityMarketPurchaseContractDescription'),
        linkedTo,
      };

      const createdDoc = await (dispatch as any)(addDocument(newDocumentData));

      if (!createdDoc?._id) {
        errorSwal(t('ErrorGeneratingContract'));
        setIsLoading(false);
        return;
      }

      const buyerId =
        buyer?._id ||
        (typeof operation.buyer === 'string' ? operation.buyer : '');
      const sellerId =
        seller?._id ||
        (typeof operation.seller === 'string' ? operation.seller : '');

      await (dispatch as any)(
        updateTradingOfferOperation(operation._id, {
          status: 'COMPLETED',
          updatedBy: user?.['_id'],
          purchaseContractDocumentId: createdDoc._id,
          buyerId,
          sellerId,
        } as any)
      );

      successSwal(t('OperationCompletedWithContract'));
      handleCloseModal();
      if (onSuccess) onSuccess();
    } catch (error) {
      errorSwal(t('ErrorGeneratingContract'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CompleteOperationModalContext.Provider
      value={{
        data: { buyerName, sellerName, isLoading },
        actions: { handleConfirm, handleCloseModal },
      }}
    >
      {children}
    </CompleteOperationModalContext.Provider>
  );
};

export const useCompleteOperationModal = () =>
  useContext(CompleteOperationModalContext);
